import { useMemo, useState } from "react";
import { Notice, useNotice } from "../components/Notice";
import { FilterBar, PageHeader, SearchField, SelectField, plural } from "../components/PageHeader";
import { EmptyState, ErrorState, Skeleton, StatusSelect } from "../components/States";
import { listLeads, setLeadStatus } from "../lib/data";
import { refreshCounts } from "../lib/events";
import { aud, fmtDate, humanise } from "../lib/format";
import { leadJob, leadSource } from "../lib/jobs";
import { useAsync } from "../lib/useAsync";
import { ConvertLead } from "../modules/clients/ClientParts";

const STATUSES = ["new", "contacted", "quoted", "won", "lost", "spam"];

export function Leads() {
  const { data, error, loading, reload } = useAsync(listLeads);
  const { notice, setNotice, fail } = useNotice();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [source, setSource] = useState("");

  const sources = useMemo(() => [...new Set((data ?? []).map((l) => l.source))].sort(), [data]);
  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (data ?? []).filter((l) => {
      if (status === "open" ? ["lost", "spam", "won"].includes(l.status) : status && l.status !== status) return false;
      if (source && l.source !== source) return false;
      if (!needle) return true;
      return [l.name, l.company, l.email, l.phone, l.site_address, l.message, ...l.services, ...(l.details ?? []).map((d) => d.value)].some((v) => v?.toLowerCase().includes(needle));
    });
  }, [data, q, status, source]);

  async function changeStatus(id: string, next: string) {
    setNotice(null);
    try {
      await setLeadStatus(id, next);
      reload();
      refreshCounts();
    } catch (e) {
      fail(e);
    }
  }

  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const l of data ?? []) c[l.status] = (c[l.status] ?? 0) + 1;
    return c;
  }, [data]);

  return (
    <section className="page" aria-labelledby="page-title">
      <PageHeader eyebrow="Sales" title="Leads" meta="Website enquiries, newest first. Latest 200 shown." />
      <Notice notice={notice} onDismiss={() => setNotice(null)} />

      <FilterBar count={data ? `Showing ${plural(rows.length, "lead")}` : undefined}>
        <SearchField label="Search leads" placeholder="Search name, company, contact or site" value={q} onChange={setQ} />
        <SelectField label="Status" value={status} onChange={setStatus} options={[
          { value: "", label: "All statuses" },
          { value: "open", label: "Open (new, contacted, quoted)" },
          ...STATUSES.map((s) => ({ value: s, label: `${humanise(s)}${counts[s] ? ` (${counts[s]})` : ""}` })),
        ]} />
        <SelectField label="Source" value={source} onChange={setSource} options={[
          { value: "", label: "All sources" },
          ...sources.map((s) => ({ value: s, label: leadSource(s) })),
        ]} />
      </FilterBar>

      {loading && !data && <Skeleton columns={6} />}
      {error && <ErrorState message={error} onRetry={reload} />}
      {data && data.length === 0 && <EmptyState title="No leads yet">Enquiries from the website forms land here.</EmptyState>}
      {data && data.length > 0 && rows.length === 0 && <EmptyState icon="search" title="No matching leads">Try a different search or status.</EmptyState>}
      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="table table--stack">
            <thead>
              <tr>
                <th scope="col">Received</th><th scope="col">Name</th><th scope="col">Contact</th>
                <th scope="col">Job</th><th scope="col" className="n">Estimate</th><th scope="col">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((l) => (
                <tr key={l.id}>
                  <td data-label="Received" className="nowrap">{fmtDate(l.created_at)}<span className="sub">{leadSource(l.source)}</span></td>
                  <td data-label="Name"><strong>{l.name}</strong>{l.company && <span className="sub">{l.company}</span>}</td>
                  <td data-label="Contact">
                    {l.phone && <a className="tel" href={`tel:${l.phone.replace(/\s/g, "")}`}>{l.phone}</a>}
                    {l.email && <a className="sub sub--link" href={`mailto:${l.email}`}>{l.email}</a>}
                  </td>
                  <td data-label="Job">
                    {leadJob(l)}
                    {l.site_address && <span className="sub">{l.site_address}</span>}
                    {l.details?.map((d) => <span key={d.key} className="sub">{d.label}: {d.value}</span>)}
                    {l.message && <span className="sub" title={l.message}>{l.message.length > 140 ? `${l.message.slice(0, 140)}...` : l.message}</span>}
                  </td>
                  <td data-label="Estimate" className="n strong">
                    {l.estimate_result?.total !== undefined ? aud(l.estimate_result.total) : <span className="muted">None</span>}
                  </td>
                  <td data-label="Status">
                    <StatusSelect value={l.status} options={STATUSES} label={`Status for ${l.name}`} onChange={(v) => changeStatus(l.id, v)} />
                    <ConvertLead lead={l} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
